import { useEffect } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@/components/TailwindComponents';

interface DeleteFormData {
  reason: string;
}

const defaultValues: DeleteFormData = {
  reason: '',
};

interface DeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
  caseData: {
    caseId: string;
    caseType: string;
    status: string;
    createdBy: string;
  } | null;
}

export const DeleteInternalAffairsCaseModal = ({
  open,
  onClose,
  onConfirm,
}: DeleteModalProps) => {
  const methods = useForm<DeleteFormData>({
    defaultValues,
    mode: 'onSubmit',
  });

  const { handleSubmit, reset } = methods;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = handleSubmit(async (data: DeleteFormData) => {
    await onConfirm(data.reason);
    handleClose();
  });

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[592px] [&>button.absolute]:text-red-500">
        <DialogHeader className="border-b pb-4">
          <DialogTitle>Delete IA Case</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 relative">
          <FormProvider {...methods}>
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <p className="text-grey-100">
                Are you sure you want to delete this IA case? This action cannot
                be undone.
              </p>

              {/* Reason field */}
              <div className="space-y-2">
                <Label htmlFor="reason" className="text-sm">
                  Reason for deletion <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="reason"
                  control={methods.control}
                  rules={{
                    required: true,
                    maxLength: {
                      value: 500,
                      message: 'Reason should be less than 500 characters.',
                    },
                  }}
                  render={({ field }) => (
                    <Textarea {...field} id="reason" rows={4} placeholder="/" />
                  )}
                />
                {methods.formState.errors.reason?.type === 'required' && (
                  <p className="text-sm text-red-500">
                    The Reason field is required
                  </p>
                )}
                {methods.formState.errors.reason?.type === 'maxLength' && (
                  <p className="text-sm text-red-500">
                    Reason should be less than 500 characters.
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  className="rounded-md bg-white"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  className="rounded-md"
                  disabled={!methods.watch('reason')?.trim()}
                >
                  Delete
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};
